import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2, Loader2, Circle, X, MapPin, Home, Image as ImageIcon,
  Sparkles, AlertTriangle, ArrowRight, Wand2, Eye, Pencil, PartyPopper,
} from "lucide-react";
import { authApi } from "./authApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);

const VIBES = [
  "Votre Agent IA fait le tri. Vous pouvez garder votre café. \u2615",
  "On garde les bonnes photos, promis. \uD83D\uDCF8",
  "Quelques secondes de plus : votre annonce prend forme. \u2728",
  "On vérifie le quartier pendant que vous ne faites rien. Plutôt pratique. \uD83D\uDE09",
  "Votre Agent IA peaufine les derniers détails\u2026",
  "Encore un petit effort\u2026 de notre côté, pas du vôtre. \uD83D\uDE0E",
  "On assemble tout ça avec soin. \uD83E\uDDF5",
];

const StepRow = ({ n, current, done, step }) => {
  const isDone = done || n < current;
  const isActive = !done && n === current;
  return (
    <div className={`flex items-start gap-3 py-2 transition-opacity ${isDone || isActive ? "opacity-100" : "opacity-45"}`} data-testid={`gen-step-${n}`}>
      <div className="mt-0.5 shrink-0">
        {isDone ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          : isActive ? <Loader2 className="w-5 h-5 text-[#C19A6B] animate-spin" />
            : <Circle className="w-5 h-5 text-zinc-600" />}
      </div>
      <div className="min-w-0">
        <p className={`text-sm ${isActive ? "text-white font-medium" : isDone ? "text-zinc-200" : "text-zinc-400"}`}>
          {n}. {step.label}
        </p>
        {(isActive) && <p className="text-[12px] text-zinc-400 mt-0.5 leading-snug">{step.desc}</p>}
      </div>
    </div>
  );
};

const ResultChips = ({ r }) => {
  if (!r) return null;
  const bien = [r.type, r.rooms ? `${r.rooms} pièces` : "", r.surface ? `${r.surface} m²` : ""].filter(Boolean).join(" • ");
  return (
    <div className="space-y-2" data-testid="gen-results">
      {r.title && (
        <div className="flex items-start gap-2 text-sm text-zinc-200"><Home className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <span><b className="text-white">Bien identifié</b>{bien ? ` — ${bien}` : ""}{r.price ? ` • ${Number(r.price).toLocaleString("fr-FR")} €` : ""}</span></div>
      )}
      {r.city && (
        <div className="flex items-center gap-2 text-sm text-zinc-200"><MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
          <span><b className="text-white">Commune</b> — {r.city}{r.postal_code ? ` (${r.postal_code})` : ""}</span></div>
      )}
      {(r.photos_kept != null) && (
        <div className="flex items-center gap-2 text-sm text-zinc-200"><ImageIcon className="w-4 h-4 text-emerald-400 shrink-0" />
          <span><b className="text-white">{r.photos_kept} photo{r.photos_kept > 1 ? "s" : ""}</b> du bien conservée{r.photos_kept > 1 ? "s" : ""}{r.photos_ignored ? ` · ${r.photos_ignored} écartée(s)` : ""}</span></div>
      )}
      {r.highlights_count > 0 && (
        <div className="flex items-center gap-2 text-sm text-zinc-200"><Sparkles className="w-4 h-4 text-emerald-400 shrink-0" />
          <span><b className="text-white">{r.highlights_count} points forts</b> générés</span></div>
      )}
    </div>
  );
};

export default function GenerationProgress({ jobId, onView, onEdit, onClose }) {
  const [job, setJob] = useState(null);
  const [vibe, setVibe] = useState(0);
  const [cityChoice, setCityChoice] = useState("");
  const [cityOther, setCityOther] = useState("");
  const [confirming, setConfirming] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const j = await authApi.importJob(jobId);
        if (!stop) setJob(j);
        if (!stop && j && (j.status === "done" || j.status === "error")) return;
      } catch (e) { /* keep polling */ }
      if (!stop) timer.current = setTimeout(poll, 1300);
    };
    poll();
    return () => { stop = true; if (timer.current) clearTimeout(timer.current); };
  }, [jobId]);

  useEffect(() => {
    const t = setInterval(() => setVibe((v) => (v + 1) % VIBES.length), 3600);
    return () => clearInterval(t);
  }, []);

  const steps = job?.steps || [];
  const current = job?.step || 1;
  const percent = job?.percent || 0;
  const status = job?.status;
  const results = job?.results || {};
  const lastEvent = (job?.events || [])[(job?.events || []).length - 1]?.msg;
  const thumbs = (results.thumbnails || []).slice(0, 8);

  const confirmCity = async () => {
    const opts = job?.city_options || [];
    let payload = null;
    if (cityChoice === "__other__") {
      if (!cityOther.trim()) return;
      payload = { city: cityOther.trim() };
    } else {
      const o = opts[Number(cityChoice)];
      if (!o) return;
      payload = o;
    }
    setConfirming(true);
    try { await authApi.importJobConfirmCity(jobId, payload); } catch (e) { /* noop */ }
    setConfirming(false);
  };

  const done = status === "done";
  const errored = status === "error";
  const awaitingCity = status === "awaiting_city";

  return (
    <div className="fixed inset-0 z-[140] bg-[#050505]/95 backdrop-blur-md flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" data-testid="generation-progress">
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-3xl my-4 rounded-[14px] border border-white/10 bg-[#0d0d10] shadow-2xl overflow-hidden">
        <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C19A6B]/15 blur-3xl pointer-events-none" />

        {/* ===== DONE ===== */}
        {done ? (
          <div className="relative p-6 sm:p-9 text-center" data-testid="gen-done">
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <PartyPopper className="w-8 h-8 text-emerald-400" />
            </motion.div>
            <h2 className="calli text-3xl sm:text-4xl text-[#C19A6B] mt-4">Votre annonce est prête !</h2>
            {(job?.completeness != null && job.completeness < 100)
              ? <p className="text-sm text-zinc-300 mt-2">Créée à <b className="text-white">{job.completeness}%</b> — quelques éléments peuvent encore être améliorés.</p>
              : <p className="text-sm text-zinc-300 mt-2">Tout est prêt. Vous pouvez la consulter ou l'affiner.</p>}

            <div className="mt-6 grid sm:grid-cols-2 gap-2.5 text-left max-w-xl mx-auto">
              {[
                results.photos_kept != null && `${results.photos_kept} photo${results.photos_kept > 1 ? "s" : ""} récupérée${results.photos_kept > 1 ? "s" : ""}`,
                (job?.summary?.description) && "Description IA créée",
                results.highlights_count > 0 && `${results.highlights_count} points forts générés`,
                results.city && `Informations sur ${results.city} ajoutées`,
                (job?.summary?.location_ok) && "Commerces et environnement analysés",
                results.staging_free > 0 && `${results.staging_free} home staging${results.staging_free > 1 ? "s" : ""} IA offert${results.staging_free > 1 ? "s" : ""} en préparation`,
              ].filter(Boolean).map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-zinc-200 bg-white/[0.03] border border-white/8 rounded-[8px] px-3 py-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> {t}
                </div>
              ))}
            </div>

            {(job?.improvements || []).length > 0 && (
              <div className="mt-4 max-w-xl mx-auto text-left border border-amber-500/25 bg-amber-500/[0.06] rounded-[10px] p-3.5">
                <p className="text-xs uppercase tracking-wider text-amber-300 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> Encore {job.improvements.length} élément(s) à compléter</p>
                <ul className="mt-2 space-y-1">
                  {job.improvements.map((im, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-300"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> {im}</li>
                  ))}
                </ul>
                <p className="text-[11px] text-zinc-500 mt-2">Vous pourrez les générer en un clic depuis <b className="text-zinc-300">Audit Annonces</b>.</p>
              </div>
            )}

            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={() => onView && onView(job.slug)} data-testid="gen-cta-view"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-semibold uppercase tracking-wider text-sm px-7 py-3.5 rounded-[8px] transition-colors">
                <Eye className="w-4 h-4" /> Voir mon annonce
              </button>
              <button onClick={() => onEdit && onEdit(job.slug)} data-testid="gen-cta-edit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B] text-white text-sm px-6 py-3.5 rounded-[8px] transition-colors">
                <Pencil className="w-4 h-4" /> Modifier mon annonce
              </button>
            </div>
          </div>

        ) : errored ? (
          /* ===== ERROR (bloquant) ===== */
          <div className="relative p-6 sm:p-9 text-center" data-testid="gen-error">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center"><AlertTriangle className="w-7 h-7 text-red-400" /></div>
            <h2 className="text-xl font-light text-white mt-4">La génération n'a pas abouti</h2>
            <p className="text-sm text-zinc-300 mt-2 max-w-md mx-auto">{job?.blocking_error || "Une erreur est survenue. Réessayez."}</p>
            <button onClick={onClose} data-testid="gen-error-close" className="mt-6 inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B] text-white text-sm px-6 py-3 rounded-[8px] transition-colors">Fermer et réessayer</button>
          </div>

        ) : (
          /* ===== EN COURS ===== */
          <div className="relative p-5 sm:p-8">
            <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" title="Réduire" data-testid="gen-close"><X className="w-5 h-5" /></button>

            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#C19A6B]" />
              <h2 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Votre Agent IA prépare votre annonce…</h2>
            </div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed max-w-2xl">
              Nous récupérons votre bien, vérifions les informations et préparons automatiquement votre annonce Off Markette.
              Gardez cette page ouverte, on s'occupe de tout. ✨
            </p>

            {/* Barre de progression */}
            <div className="mt-5">
              <div className="flex items-end justify-between mb-1.5">
                <span className="text-xs text-zinc-400" data-testid="gen-step-label">Étape {Math.min(current, steps.length)} sur {steps.length || 10}{job?.step_label ? ` — ${job.step_label}` : ""}</span>
                <span className="text-2xl font-light text-white tabular-nums" data-testid="gen-percent">{percent}%</span>
              </div>
              <div className="h-3 w-full bg-white/8 rounded-full overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-[#C19A6B] to-[#e0c79a] rounded-full"
                  animate={{ width: `${percent}%` }} transition={{ duration: 0.6, ease: "easeOut" }} data-testid="gen-bar" />
              </div>
              <div className="mt-2 min-h-[18px]">
                <AnimatePresence mode="wait">
                  <motion.p key={lastEvent || vibe} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3 }}
                    className="text-[12.5px] text-[#C19A6B]/90" data-testid="gen-vibe">
                    {awaitingCity ? "En attente de votre confirmation…" : (lastEvent || VIBES[vibe])}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>

            {/* Confirmation de ville */}
            {awaitingCity && (
              <div className="mt-4 border border-[#C19A6B]/40 bg-[#C19A6B]/[0.08] rounded-[10px] p-4" data-testid="gen-city-confirm">
                <p className="text-sm text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C19A6B]" /> Quelle est la commune exacte du bien ?</p>
                <div className="mt-3 space-y-1.5">
                  {(job.city_options || []).map((o, i) => (
                    <label key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] border cursor-pointer transition-colors ${cityChoice === String(i) ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/25"}`} data-testid={`gen-city-opt-${i}`}>
                      <input type="radio" name="city" checked={cityChoice === String(i)} onChange={() => setCityChoice(String(i))} className="accent-[#C19A6B]" />
                      <span className="text-sm text-zinc-200">{o.city}{o.postal_code ? ` (${o.postal_code})` : ""}{o.department ? ` — ${o.department}` : ""}</span>
                    </label>
                  ))}
                  <label className={`flex items-center gap-2.5 px-3 py-2 rounded-[8px] border cursor-pointer transition-colors ${cityChoice === "__other__" ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/25"}`}>
                    <input type="radio" name="city" checked={cityChoice === "__other__"} onChange={() => setCityChoice("__other__")} className="accent-[#C19A6B]" />
                    <span className="text-sm text-zinc-200">Autre :</span>
                    <input value={cityOther} onChange={(e) => { setCityOther(e.target.value); setCityChoice("__other__"); }} placeholder="Nom de la commune" className="flex-1 bg-white/5 border border-white/10 rounded-[6px] px-2 py-1 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="gen-city-other" />
                  </label>
                </div>
                <button onClick={confirmCity} disabled={confirming || !cityChoice} data-testid="gen-city-confirm-btn"
                  className="mt-3 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-[8px] transition-colors">
                  {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Confirmer et continuer
                </button>
              </div>
            )}

            <div className="mt-5 grid md:grid-cols-2 gap-5">
              {/* Étapes */}
              <div className="border border-white/8 rounded-[10px] p-3 bg-white/[0.02]" data-testid="gen-steps">
                {steps.map((s, i) => <StepRow key={s.key} n={i + 1} current={current} done={done} step={s} />)}
              </div>
              {/* Résultats + miniatures */}
              <div className="space-y-4">
                {thumbs.length > 0 && (
                  <div data-testid="gen-thumbs">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> {results.photos_kept || thumbs.length} photos récupérées</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {thumbs.map((u, i) => (
                        <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                          className="aspect-square overflow-hidden rounded-[6px] border border-white/10 bg-white/5">
                          <img src={abs(u)} alt="" loading="lazy" className="w-full h-full object-cover" />
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                <ResultChips r={results} />
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
